import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Cookie } from "lucide-react";

const CONSENT_KEY = "canales.cookie_consent_v1";
// Custom event fired when a user hits "Cookie preferences" from the footer,
// so the singleton banner shows itself again without a page reload.
const RESET_EVENT = "canales:cookie-consent:reset";

export function resetCookieConsent() {
  localStorage.removeItem(CONSENT_KEY);
  window.dispatchEvent(new CustomEvent(RESET_EVENT));
}

export function getCookieConsent() {
  try {
    return JSON.parse(localStorage.getItem(CONSENT_KEY) || "null");
  } catch {
    return null;
  }
}

export default function CookieConsent() {
  const { t } = useTranslation();
  const location = useLocation();
  const [visible, setVisible] = useState(() => !localStorage.getItem(CONSENT_KEY));

  useEffect(() => {
    const onReset = () => setVisible(true);
    window.addEventListener(RESET_EVENT, onReset);
    return () => window.removeEventListener(RESET_EVENT, onReset);
  }, []);

  const decide = (choice) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ choice, at: new Date().toISOString() }));
    setVisible(false);
  };

  // Hide on the immersive full-screen game routes so the banner never overlaps
  // the host control bar (public /play/:token and authed /games/:id/play).
  const path = location.pathname || "";
  const isGameStage =
    path.startsWith("/play/") ||
    path.startsWith("/join") ||
    /^\/games\/[^/]+\/play$/.test(path) ||
    /^\/games\/[^/]+\/host$/.test(path);
  if (isGameStage) return null;

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-40 bg-[#0B1220] text-white border-t border-white/10 shadow-2xl"
      role="dialog"
      aria-label={t("cookies.title")}
      data-testid="cookie-banner"
    >
      <div className="max-w-[1500px] mx-auto px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <Cookie size={18} className="text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold">{t("cookies.title")}</p>
            <p className="text-xs text-stone-300 mt-0.5 leading-relaxed">
              {t("cookies.text")}{" "}
              <Link
                to="/privacy"
                className="underline text-stone-200 hover:text-white"
                data-testid="cookie-privacy-link"
              >
                {t("cookies.more")}
              </Link>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => decide("essential")}
            className="px-4 py-2 rounded-full text-xs sm:text-sm border border-white/25 text-stone-200 hover:bg-white/10 transition-colors"
            data-testid="cookie-accept-essential"
          >
            {t("cookies.essential")}
          </button>
          <button
            type="button"
            onClick={() => decide("all")}
            className="px-5 py-2 rounded-full text-xs sm:text-sm font-semibold bg-amber-400 text-stone-900 hover:bg-amber-300 transition-colors"
            data-testid="cookie-accept-all"
          >
            {t("cookies.accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
